import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherArrowLeft, FeatherTrash2, FeatherAlertTriangle, FeatherCheckCircle, FeatherXCircle, FeatherSearch, FeatherLoader } from "@subframe/core";
import { getGlsList, deleteGls, GLListItem, DeleteGlsResponse } from "../../data/api/trialBalanceApi";

export const DeleteGlsPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();
  
  const [glsList, setGlsList] = useState<GLListItem[]>([]);
  const [filteredGlsList, setFilteredGlsList] = useState<GLListItem[]>([]);
  const [selectedGlIds, setSelectedGlIds] = useState<number[]>([]);
  const [isLoadingGls, setIsLoadingGls] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Load GLs list on component mount
  useEffect(() => {
    loadGlsList();
  }, []);

  // Filter GLs based on search
  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredGlsList(glsList);
    } else {
      const filter = searchFilter.toLowerCase();
      const filtered = glsList.filter(
        (gl) =>
          gl.ledger_name.toLowerCase().includes(filter) ||
          (gl.class_1 || "").toLowerCase().includes(filter) ||
          (gl.class_2 || "").toLowerCase().includes(filter)
      );
      setFilteredGlsList(filtered);
    }
  }, [searchFilter, glsList]);

  const loadGlsList = async () => {
    setIsLoadingGls(true);
    setError("");
    try {
      const response = await getGlsList();
      if (response.success) {
        setGlsList(response.data);
        setFilteredGlsList(response.data);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load GLs list");
    } finally {
      setIsLoadingGls(false);
    }
  };

  const handleSelectGl = (glId: number) => {
    setSelectedGlIds((prev) =>
      prev.includes(glId) ? prev.filter((id) => id !== glId) : [...prev, glId]
    );
  };

  const handleSelectAll = () => {
    if (selectedGlIds.length === filteredGlsList.length) {
      setSelectedGlIds([]);
    } else {
      setSelectedGlIds(filteredGlsList.map((gl) => gl.id));
    }
  };

  const handleDeleteClick = () => {
    if (selectedGlIds.length === 0) {
      setError("Please select at least one GL account to delete.");
      return;
    }
    setShowConfirmDialog(true);
    setDeleteConfirmation("");
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation !== "DELETE") {
      setError("Please type 'DELETE' to confirm.");
      return;
    }

    setIsDeleting(true);
    setError("");
    setSuccess("");

    try {
      const response: DeleteGlsResponse = await deleteGls({
        gl_ids: selectedGlIds,
      });

      if (response.success) {
        setSuccess(
          `Successfully deleted ${response.data.deleted_count} GL account(s). ` +
          `${response.data.tb_entries_deleted} Trial Balance entries, ` +
          `${response.data.levels_deleted} levels, and ` +
          `${response.data.tags_deleted} tags were also deleted.`
        );
        setSelectedGlIds([]);
        setShowConfirmDialog(false);
        setDeleteConfirmation("");
        // Reload the list
        await loadGlsList();
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete GLs");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
    setDeleteConfirmation("");
    setError("");
  };

  const selectedGlsWithTbEntries = filteredGlsList.filter(
    (gl) => selectedGlIds.includes(gl.id) && gl.has_tb_entries
  );
  const totalTbEntriesToDelete = selectedGlsWithTbEntries.reduce(
    (sum, gl) => sum + gl.tb_entries_count,
    0
  );

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/cfo/tb/upload_mapping")}
            >
              Back to Upload
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto p-8">
          {/* Back Button */}
          <div className="flex w-full items-start mb-4">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/cfo/tb/upload_mapping")}
            >
              Back to Upload
            </Button>
          </div>

          {/* Page Title */}
          <div className="flex w-full flex-col items-start gap-4 mb-6">
            <h1 className="text-heading-1 font-heading-1 text-default-font">
              Delete GL Accounts
            </h1>
            <p className="text-body font-body text-subtext-color">
              Manage and delete General Ledger accounts. Deleting a GL will also remove all associated Trial Balance entries, Levels, and Tags.
            </p>
          </div>

          {/* Warning Banner */}
          <div className="flex w-full items-start gap-3 rounded-md border border-solid border-red-200 bg-red-50 p-4 mb-6">
            <FeatherAlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex flex-col items-start gap-1">
              <span className="text-body font-body font-semibold text-red-900">
                Warning: This action cannot be undone
              </span>
              <span className="text-caption font-caption text-red-700">
                Deleting GL accounts will permanently remove them and all associated data (Levels, Tags). 
                If a GL has Trial Balance entries, those entries will also be deleted.
              </span>
            </div>
          </div>

          {/* Search and Actions Bar */}
          <div className="flex w-full items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <div className="relative flex-1">
                <FeatherSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-subtext-color" />
                <input
                  type="text"
                  placeholder="Search by ledger name, class 1, or class 2..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-solid border-neutral-border rounded-md bg-default-background text-body font-body text-default-font"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-secondary"
                onClick={handleSelectAll}
                disabled={isLoadingGls || isDeleting}
              >
                {selectedGlIds.length === filteredGlsList.length && filteredGlsList.length > 0
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <Button
                variant="destructive-primary"
                icon={<FeatherTrash2 />}
                onClick={handleDeleteClick}
                disabled={selectedGlIds.length === 0 || isLoadingGls || isDeleting}
              >
                Delete Selected ({selectedGlIds.length})
              </Button>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-red-200 bg-red-50 p-4 mb-4">
              <FeatherXCircle className="text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-body font-body text-red-900">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-green-200 bg-green-50 p-4 mb-4">
              <FeatherCheckCircle className="text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-body font-body text-green-900">{success}</span>
            </div>
          )}

          {/* GLs Table */}
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background">
            {isLoadingGls ? (
              <div className="flex w-full items-center justify-center p-12">
                <FeatherLoader className="animate-spin text-subtext-color" />
                <span className="ml-3 text-body font-body text-subtext-color">Loading GLs...</span>
              </div>
            ) : filteredGlsList.length === 0 ? (
              <div className="flex w-full items-center justify-center p-12">
                <span className="text-body font-body text-subtext-color">
                  {searchFilter ? "No GLs found matching your search." : "No GL accounts found."}
                </span>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-solid border-neutral-border bg-neutral-50">
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        <input
                          type="checkbox"
                          checked={selectedGlIds.length === filteredGlsList.length && filteredGlsList.length > 0}
                          onChange={handleSelectAll}
                          className="cursor-pointer"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        Ledger Name
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        Class 1
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        Class 2
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        TB Entries
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-caption font-semibold text-subtext-color">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGlsList.map((gl) => {
                      const isSelected = selectedGlIds.includes(gl.id);
                      const hasTbEntries = gl.has_tb_entries;
                      return (
                        <tr
                          key={gl.id}
                          className={`border-b border-solid border-neutral-border ${
                            hasTbEntries ? "bg-red-50" : ""
                          } ${isSelected ? "bg-blue-50" : ""} hover:bg-neutral-50`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectGl(gl.id)}
                              className="cursor-pointer"
                              disabled={isDeleting}
                            />
                          </td>
                          <td className="px-4 py-3 text-body font-body text-default-font">
                            {gl.ledger_name}
                          </td>
                          <td className="px-4 py-3 text-body font-body text-default-font">
                            {gl.class_1 || "-"}
                          </td>
                          <td className="px-4 py-3 text-body font-body text-default-font">
                            {gl.class_2 || "-"}
                          </td>
                          <td className="px-4 py-3">
                            {hasTbEntries ? (
                              <Badge variant="error">{gl.tb_entries_count} entries</Badge>
                            ) : (
                              <Badge variant="neutral">No entries</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {hasTbEntries ? (
                              <div className="flex items-center gap-2">
                                <FeatherAlertTriangle className="text-red-600" />
                                <span className="text-caption font-caption text-red-700">
                                  Has TB entries
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <FeatherCheckCircle className="text-green-600" />
                                <span className="text-caption font-caption text-green-700">
                                  Safe to delete
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Summary */}
          {selectedGlIds.length > 0 && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-yellow-200 bg-yellow-50 p-4 mt-4">
              <FeatherAlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col items-start gap-1">
                <span className="text-body font-body font-semibold text-yellow-900">
                  {selectedGlIds.length} GL account(s) selected for deletion
                </span>
                {selectedGlsWithTbEntries.length > 0 && (
                  <span className="text-caption font-caption text-yellow-700">
                    {selectedGlsWithTbEntries.length} of these GLs have Trial Balance entries. 
                    {totalTbEntriesToDelete} total TB entries will be deleted.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-default-background rounded-lg border border-solid border-neutral-border p-6 max-w-md w-full mx-4">
              <div className="flex flex-col items-start gap-4">
                <div className="flex items-start gap-3">
                  <FeatherAlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col items-start gap-2">
                    <h2 className="text-heading-3 font-heading-3 text-default-font">
                      Confirm Deletion
                    </h2>
                    <p className="text-body font-body text-subtext-color">
                      You are about to delete <strong>{selectedGlIds.length}</strong> GL account(s).
                    </p>
                    {selectedGlsWithTbEntries.length > 0 && (
                      <p className="text-body font-body text-red-700">
                        <strong>{selectedGlsWithTbEntries.length}</strong> of these GLs have Trial Balance entries. 
                        <strong> {totalTbEntriesToDelete} total TB entries</strong> will be deleted.
                      </p>
                    )}
                    <p className="text-body font-body text-subtext-color mt-2">
                      This action cannot be undone. Type <strong>DELETE</strong> to confirm:
                    </p>
                  </div>
                </div>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="w-full px-4 py-2 border border-solid border-neutral-border rounded-md bg-default-background text-body font-body text-default-font"
                  disabled={isDeleting}
                />
                <div className="flex w-full items-center justify-end gap-2">
                  <Button
                    variant="neutral-secondary"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive-primary"
                    icon={<FeatherTrash2 />}
                    onClick={handleConfirmDelete}
                    disabled={deleteConfirmation !== "DELETE" || isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
